import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Briefcase, TrendingUp, Target, ArrowRight, ArrowLeft,
  Building2, Lightbulb, ExternalLink, BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (v, ccy = 'EUR') => {
  if (v === null || v === undefined) return '—';
  try {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: ccy || 'EUR',
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v} ${ccy}`;
  }
};

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  committed: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-slate-200 text-slate-700 border-slate-300',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
};

const MODULE_LABEL = {
  participatory_planning: 'Participatory Planning',
  blended_investments: 'Combined Investments',
  supply_demand_matching: 'Supply & Demand',
};

/**
 * InvestorPortfolio — "My Investments" dashboard.
 *
 * Aggregates all funding commitments the authenticated investor holds
 * across every project, plus any Social Investment Registrations they
 * have proposed. Provides a one-glance view of impact-portfolio health.
 */
const InvestorPortfolio = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/investor-dashboard/my-portfolio`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load portfolio');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-slate-500"
           data-testid="portfolio-loading">
        Loading your portfolio…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const ccy = t.currency || 'EUR';

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="investor-portfolio">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-700" /> My Investments
          </h1>
          <p className="text-sm text-slate-500">
            One-glance view of your committed funding, drawdowns and proposals across
            the ISISP platform.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" data-testid="back-to-dashboard-link">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
            </Button>
          </Link>
          <Link to="/social-investment-registration">
            <Button variant="outline" data-testid="propose-investment-link">
              <Lightbulb className="h-4 w-4 mr-1" /> Propose an investment
            </Button>
          </Link>
        </div>
      </div>

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-committed">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">Total committed</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">
              {fmtCurrency(t.total_committed, ccy)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {t.project_count || 0} project(s) · {t.commitment_count || 0} commitment(s)
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-drawn">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">Disbursed</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">
              {fmtCurrency(t.total_drawn, ccy)}
            </div>
            <div className="text-xs text-slate-500 mt-1">{t.progress_pct}% of commitments</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-remaining">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">Available to draw</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">
              {fmtCurrency(t.total_remaining, ccy)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Ready for milestone-linked tranches</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Portfolio progress</div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                style={{ width: `${Math.min(t.progress_pct || 0, 100)}%` }}
              />
            </div>
            <div className="text-xs text-slate-500 mt-1.5">{t.progress_pct}% disbursed</div>
          </CardContent>
        </Card>
      </div>

      {/* Two-column main */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Projects table */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-indigo-600" /> Funded projects
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(!data.projects || data.projects.length === 0) ? (
              <div className="p-6 text-sm text-slate-500 text-center">
                You don&apos;t hold any active commitments yet.
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-2 text-left">Project</th>
                      <th className="px-4 py-2 text-right">Committed</th>
                      <th className="px-4 py-2 text-right">Drawn</th>
                      <th className="px-4 py-2 text-right">Remaining</th>
                      <th className="px-4 py-2 text-left w-40">Progress</th>
                      <th className="px-4 py-2">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((p) => (
                      <tr key={p.project_id} className="border-t" data-testid={`portfolio-row-${p.project_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{p.project_title}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            {p.project_category && (
                              <Badge variant="outline" className="text-xs">
                                {p.project_category}
                              </Badge>
                            )}
                            <Badge variant="outline" className={`text-xs ${STATUS_BADGE[p.commitment_status] || 'bg-slate-100'}`}>
                              {p.commitment_status}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {fmtCurrency(p.committed, p.currency)}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-700 font-medium">
                          {fmtCurrency(p.drawn, p.currency)}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-700 font-medium">
                          {fmtCurrency(p.remaining, p.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                              style={{ width: `${Math.min(p.progress_pct, 100)}%` }}
                            />
                          </div>
                          <div className="text-xs text-slate-500 mt-1">{p.progress_pct}%</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/projects/${p.project_id}?tab=funding`}
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            data-testid={`open-project-${p.project_id}`}
                          >
                            View <ArrowRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SDG breakdown */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-rose-600" /> Impact distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.sdg_breakdown || data.sdg_breakdown.length === 0) ? (
              <p className="text-sm text-slate-500 text-center">No SDG-tagged commitments yet.</p>
            ) : (
              <div className="space-y-3">
                {data.sdg_breakdown.map((row) => {
                  const pct = t.total_committed > 0
                    ? Math.round((row.committed / t.total_committed) * 100)
                    : 0;
                  return (
                    <div key={row.sdg}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700">
                          SDG&nbsp;{row.sdg} · {row.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {fmtCurrency(row.committed, ccy)} · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
                        <div
                          className="h-full bg-gradient-to-r from-rose-500 to-orange-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Investment proposals */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-600" /> My investment proposals
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Social Investment Registrations you proposed. They are automatically
            engaged in Participatory Planning, Combined Investments and Supply &amp; Demand Matching.
          </p>
        </CardHeader>
        <CardContent>
          {(!data.proposals || data.proposals.length === 0) ? (
            <div className="text-center py-6">
              <Lightbulb className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 mb-3">
                You haven&apos;t proposed any investments yet.
              </p>
              <Link to="/social-investment-registration">
                <Button variant="outline" data-testid="propose-first-investment-btn">
                  <Lightbulb className="h-4 w-4 mr-1" /> Propose your first investment
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {data.proposals.map((p) => (
                <div
                  key={p.id}
                  className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition"
                  data-testid={`proposal-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-slate-900 truncate">{p.title}</div>
                      <Badge variant="outline" className="text-xs bg-slate-100">
                        {p.status}
                      </Badge>
                      {(p.engaged_modules || []).map((m) => (
                        <Badge key={m} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                          {MODULE_LABEL[m] || m}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      {p.sector && <span><Building2 className="h-3 w-3 inline -mt-0.5" /> {p.sector}</span>}
                      {p.estimated_budget > 0 && (
                        <span>
                          <BarChart3 className="h-3 w-3 inline -mt-0.5" />{' '}
                          {fmtCurrency(p.estimated_budget, p.currency || 'EUR')}
                        </span>
                      )}
                      {p.investment_type && <span>· {p.investment_type}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Link
                      to={`/social-investment-registration?open=${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      data-testid={`open-proposal-${p.id}`}
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                    {p.linked_blended_plan_id && (
                      <Link
                        to="/combined-investments"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                      >
                        view plan <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestorPortfolio;
